/**
 * AgentLandingPage — per-agent SEO/conversion landing.
 *
 * URL: /agent/:slug (e.g. /agent/helpdesk)
 * Reads from /src/data/agentCatalog.js so adding a new agent = one
 * data row, zero JSX changes.
 *
 * Layout (Mika's "Profitron Bot"-inspired emotional copy style):
 *   1. Hero — agent name + tagline + emotional CTA + demo link
 *   2. Pain quote (red border, white-on-black) — the friction quote
 *   3. "Čo ti ušetrí" 3-column: čas / peniaze / nervy
 *   4. "Čo robí za teba" bullet list
 *   5. Comparison table — manuál vs. agent (savings %)
 *   6. Closing CTA — emotional + dual button (Demo / Rent)
 */
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, Euro, Heart, Check, AlertTriangle } from "lucide-react";
import { findAgent } from "../data/agentCatalog";
import AIConciergeWidget from "../components/AIConciergeWidget";
import ComingSoonWaitlist from "../components/agent-os/ComingSoonWaitlist";
import DemandCounter from "../components/agent-os/DemandCounter";
import VoteForAgentWidget from "../components/agent-os/VoteForAgentWidget";
import EarlyBirdCounter from "../components/agent-os/EarlyBirdCounter";
import LaunchCountdownWidget from "../components/agent-os/LaunchCountdownWidget";

export default function AgentLandingPage() {
  const { slug } = useParams();
  const agent = findAgent(slug);

  if (!agent) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-black mb-4">Agent nenájdený</h1>
          <Link to="/prenajmi-si-agenta" className="text-amber-400 underline">
            ← Späť na katalóg agentov
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid={`agent-landing-${agent.slug}`}>
      <ReferralBanner />
      {/* ─── Hero ─── */}
      <section className="border-b border-gray-900 px-6 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto">
          <Link to="/prenajmi-si-agenta" className="text-[11px] text-gray-500 hover:text-amber-400 uppercase tracking-widest mb-4 inline-block">
            ← Všetkých 14 agentov
          </Link>
          <div className="flex items-start gap-4 mb-6">
            <div className="text-6xl">{agent.emoji}</div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: agent.accent }}>
                {agent.coming_soon ? `🔬 Pripravujeme & testujeme · ETA ${agent.eta}` : "AI Agent · TopRankPulse"}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">{agent.name}</h1>
            </div>
          </div>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
            {agent.tagline}
          </p>
          {agent.coming_soon ? (
            <div className="max-w-xl space-y-3">
              <LaunchCountdownWidget
                agentSlug={agent.slug}
                onReserveClick={() => {
                  // Scroll to vote widget
                  const el = document.querySelector(`[data-testid="vote-widget-${agent.slug}"]`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  const emailInput = document.querySelector('[data-testid="vote-email-input"]');
                  if (emailInput) setTimeout(() => emailInput.focus(), 600);
                }}
              />
              <VoteForAgentWidget
                agentSlug={agent.slug}
                agentName={agent.name}
                emoji={agent.emoji}
                priceMonthEur={agent.price || 0}
                accent={agent.accent}
              />
              <DemandCounter agentSlug={agent.slug} />
              <details className="text-xs text-zinc-500">
                <summary className="cursor-pointer hover:text-amber-400">
                  Chceš poslať detailný feedback? Otvor full waitlist formulár →
                </summary>
                <div className="mt-3">
                  <ComingSoonWaitlist
                    agentSlug={agent.slug}
                    agentName={agent.name}
                    eta={agent.eta}
                    variant="full"
                    accent={agent.accent}
                  />
                </div>
              </details>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <Link to={agent.demo_url}
                data-testid="agent-landing-demo-btn"
                data-cta
                className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-6 py-3 text-sm uppercase tracking-widest hover:bg-amber-400 transition-colors">
                Vyskúšaj demo zadarmo <ArrowRight className="w-4 h-4" />
              </Link>
              {/* V218.9 — SEO Indexer Agent has self-serve "Connect Site" form */}
              {agent.slug === "seo-indexer" ? (
                <Link to="/agents/seo-indexer/connect"
                  data-testid="agent-landing-connect-btn"
                  className="inline-flex items-center gap-2 bg-cyan-500 text-black font-black px-6 py-3 text-sm uppercase tracking-widest hover:bg-cyan-400 transition-colors">
                  Pripoj sitemap · 14 dní zadarmo
                </Link>
              ) : (
                <Link to="/agent-os/onboard"
                  data-testid="agent-landing-rent-btn"
                  className="inline-flex items-center gap-2 border border-white text-white font-black px-6 py-3 text-sm uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
                  Prenajmi za €{agent.price_eur_month}/mes
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ─── Pain quote ─── */}
      <section className="px-6 py-12 border-b border-gray-900 bg-red-950/10">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-black mb-3">
            <AlertTriangle className="w-3 h-3 inline mr-1.5" />
            Realita bez agenta
          </p>
          <p className="text-2xl sm:text-3xl font-black leading-tight text-white">
            "{agent.pain_quote}"
          </p>
        </div>
      </section>

      {/* ─── Čo ti ušetrí ─── */}
      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: agent.accent }}>
            Čo ti ušetrí
          </p>
          <h2 className="text-3xl sm:text-4xl font-black mb-10">
            Čas, peniaze, sily — všetko naraz.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-800 p-6" data-testid="saves-time">
              <Clock className="w-8 h-8 mb-3 text-amber-400" />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Čas / týždeň</p>
              <p className="text-4xl font-black mb-2">{agent.saves.time_hours_per_week}h</p>
              <p className="text-xs text-gray-400">{agent.saves.time_hours_per_week * 52}h ročne. To je {Math.round(agent.saves.time_hours_per_week * 52 / 8)} pracovných dní.</p>
            </div>
            <div className="border border-gray-800 p-6" data-testid="saves-money">
              <Euro className="w-8 h-8 mb-3 text-emerald-400" />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Peniaze / mesiac</p>
              <p className="text-4xl font-black mb-2">€{agent.saves.money_eur_per_month.toLocaleString("sk-SK")}</p>
              <p className="text-xs text-gray-400">€{(agent.saves.money_eur_per_month * 12).toLocaleString("sk-SK")} ročne. Agent stojí €{agent.price_eur_month}/mes.</p>
            </div>
            <div className="border border-gray-800 p-6" data-testid="saves-energy">
              <Heart className="w-8 h-8 mb-3 text-red-400" />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-2">Sily / pokoj</p>
              <p className="text-sm text-gray-200 leading-relaxed">{agent.saves.energy}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Čo robí za teba ─── */}
      <section className="px-6 py-16 border-t border-gray-900 bg-neutral-950">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: agent.accent }}>
            Čo robí za teba
          </p>
          <h2 className="text-3xl sm:text-4xl font-black mb-10">
            Aby si sa už nemusel.
          </h2>
          <ul className="space-y-3" data-testid="agent-tasks-list">
            {agent.does_for_you.map((task, i) => (
              <li key={i} className="flex items-start gap-3 border border-gray-800 p-4">
                <Check className="w-5 h-5 mt-0.5 shrink-0" style={{ color: agent.accent }} />
                <p className="text-base text-gray-200">{task}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Comparison table ─── */}
      <section className="px-6 py-16 border-t border-gray-900">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.2em] font-black mb-3" style={{ color: agent.accent }}>
            Manuálne vs. agent
          </p>
          <h2 className="text-3xl sm:text-4xl font-black mb-10">
            Rozdiel: {agent.vs_manual.saves_pct}% efektivity.
          </h2>
          <div className="border border-gray-800 overflow-x-auto" data-testid="comparison-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-900">
                  <th className="px-6 py-4 text-left text-[11px] uppercase tracking-widest font-black text-gray-400 w-1/2">Aspekt</th>
                  <th className="px-6 py-4 text-left text-[11px] uppercase tracking-widest font-black text-red-400">Bez agenta</th>
                  <th className="px-6 py-4 text-left text-[11px] uppercase tracking-widest font-black" style={{ color: agent.accent }}>S agentom</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                <tr className="border-t border-gray-900">
                  <td className="px-6 py-4 font-bold">Workflow</td>
                  <td className="px-6 py-4 text-gray-400">{agent.vs_manual.manual}</td>
                  <td className="px-6 py-4 font-bold">{agent.vs_manual.with_agent}</td>
                </tr>
                <tr className="border-t border-gray-900">
                  <td className="px-6 py-4 font-bold">Mesačné náklady</td>
                  <td className="px-6 py-4 text-gray-400">~€{Math.round(agent.saves.money_eur_per_month / 10) * 10}</td>
                  <td className="px-6 py-4 font-bold text-emerald-400">€{agent.price_eur_month}</td>
                </tr>
                <tr className="border-t border-gray-900">
                  <td className="px-6 py-4 font-bold">Úspora</td>
                  <td className="px-6 py-4 text-gray-400">0%</td>
                  <td className="px-6 py-4 font-black text-2xl" style={{ color: agent.accent }}>{agent.vs_manual.saves_pct}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ─── */}
      <section className="px-6 py-20 border-t border-gray-900 bg-gradient-to-b from-black to-neutral-950">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl sm:text-3xl font-black mb-4 leading-tight">
            👉 {agent.cta_emotional}
          </p>
          {agent.coming_soon ? (
            <>
              <p className="text-sm text-gray-400 mb-8">
                🔬 Agent v pilot testovaní · ETA {agent.eta} · waitlist členovia získajú 30 dní zadarmo + -25% lifetime.
              </p>
              <div className="max-w-xl mx-auto text-left">
                <ComingSoonWaitlist
                  agentSlug={agent.slug}
                  agentName={agent.name}
                  eta={agent.eta}
                  variant="full"
                  accent={agent.accent}
                />
              </div>
              <Link to="/prenajmi-si-agenta" className="text-amber-400 text-sm mt-6 inline-block hover:underline">
                ← Vráť sa k live agentom
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-8">
                14 dní zadarmo. Bez karty. €{agent.price_eur_month}/mes po triale, zruš kedykoľvek.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to={agent.demo_url}
                  data-testid="agent-landing-cta-demo"
                  className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-8 py-4 text-sm uppercase tracking-widest hover:bg-amber-400">
                  Demo zadarmo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/agent-os/onboard"
                  data-testid="agent-landing-cta-rent"
                  data-cta
                  className="inline-flex items-center gap-2 border border-white text-white font-black px-8 py-4 text-sm uppercase tracking-widest hover:bg-white hover:text-black">
                  Prenajmi agenta
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* AI Concierge widget — behavior-triggered nudge */}
      <AIConciergeWidget />
    </div>
  );
}

/* V162 — Banner shown when visitor lands via ?ref=<code>.
   Builds social proof: "Tvoj kamarát Ti odporúča tohto agenta". */
function ReferralBanner() {
  if (typeof window === "undefined") return null;
  let code = "";
  try {
    code = (new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase();
  } catch { /* noop */ }
  if (!code) return null;
  return (
    <div
      className="bg-gradient-to-r from-purple-950 to-amber-950 border-b border-amber-500/30 px-6 py-3"
      data-testid="referral-banner"
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
        <span className="text-2xl">🎁</span>
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-bold text-amber-300">
            Tvoj kamarát ti odporúča tohto agenta
          </p>
          <p className="text-[11px] text-amber-100/70">
            Keď zahlasuješ, on/ona sa priblíži k lifetime 50% zľave. Ty získaš 25% lifetime + 30 dní zadarmo pri launchi.
          </p>
          <div className="mt-1.5">
            <EarlyBirdCounter compact />
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-black text-amber-400 bg-amber-500/10 px-2 py-1 border border-amber-500/30">
          ref: {code}
        </span>
      </div>
    </div>
  );
}
